import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CartTriggerProps {
  itemCount: number;
  onClick: () => void;
  isHighlighted?: boolean;
  shouldPulse?: boolean;
  className?: string;
}

export function CartTrigger({
  itemCount,
  onClick,
  isHighlighted = false,
  shouldPulse = false,
  className,
}: CartTriggerProps) {
  if (itemCount <= 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] left-1/2 z-40 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 md:right-5',
        className
      )}
    >
      <Button
        type="button"
        size="md"
        onClick={onClick}
        className={cn(
          'pointer-events-auto h-12 rounded-full border border-brand-900 bg-brand-950 px-4 text-white shadow-xl shadow-brand-950/24',
          'hover:bg-brand-900 focus-visible:ring-brand-500',
          isHighlighted && 'animate-cart-bump',
          shouldPulse && 'animate-cart-idle'
        )}
        aria-label={`Open cart. ${itemCount} item${itemCount === 1 ? '' : 's'} in cart`}
        data-testid="cart-trigger"
      >
        <span className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/12 text-white">
            <ShoppingCart className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold">Cart</span>
          <span
            key={itemCount}
            className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-brand-600 px-2 py-1 text-xs font-semibold text-white animate-cart-count"
            aria-live="polite"
          >
            {itemCount}
          </span>
        </span>
      </Button>
    </div>
  );
}

export default CartTrigger;
